import { canonicalPayorByID, payorRefs } from './finance';
import { canonicalSiteByID, siteRefs } from './organizations';
import { CanonicalSubmission, normalizeSubmission } from './shared';

const submissionDefaults: Omit<
  CanonicalSubmission,
  'id' | 'filename' | 'uploaded' | 'status' | 'createdAt' | 'siteID' | 'site'
> = {
  payorID: null,
  startingClaim: null,
  claims: [],
  cognitoID: undefined,
  p1File: '',
  s3Path: null,
  submittedAt: null,
  totalAmount: null,
  payor: undefined,
};

const makeSubmissionSeed = (
  submission: Pick<
    CanonicalSubmission,
    'id' | 'filename' | 'uploaded' | 'status' | 'createdAt' | 'siteID' | 'site'
  > &
    Partial<CanonicalSubmission>
): CanonicalSubmission => ({
  ...submissionDefaults,
  ...submission,
});

const submissionSeeds = [
  makeSubmissionSeed({
    id: 'd2a97903-066e-42a5-aff0-4aadffca0da0',
    filename: 'QDOC038H.008',
    uploaded: false,
    status: 'created',
    createdAt: '2025-02-25T21:12:48.367Z',
    siteID: siteRefs.qdocVirtualHealthcare.id,
    payorID: payorRefs.mb.id,
    startingClaim: '000252199',
    cognitoID: '5c8d35a8-b0e1-706c-fbdf-87b1e93af252',
    submittedAt: null,
    s3Path: null,
    totalAmount: '91207.0',
    p1File: '',
    site: canonicalSiteByID.get(siteRefs.qdocVirtualHealthcare.id)!,
    payor: canonicalPayorByID.get(payorRefs.mb.id)!,
    claims: [],
  }),
  makeSubmissionSeed({
    id: '37c6a4c6-8339-4a6c-8651-330735e2f160',
    filename: 'QDOC038H.015',
    uploaded: true,
    status: 'submitted',
    createdAt: '2025-03-14T18:47:05.363Z',
    siteID: siteRefs.qdocVirtualHealthcare.id,
    payorID: payorRefs.mb.id,
    startingClaim: '000252244',
    cognitoID: '5c9d35f8-c061-7065-5652-a9066bed47cd',
    submittedAt: '2025-03-14T18:50:13.369Z',
    s3Path: null,
    totalAmount: '126448.0',
    p1File: '',
    site: canonicalSiteByID.get(siteRefs.qdocVirtualHealthcare.id)!,
    payor: canonicalPayorByID.get(payorRefs.mb.id)!,
    claims: [],
  }),
  makeSubmissionSeed({
    id: 'ddf60c79-12f7-495a-86e5-c414c685067a',
    filename: 'QDOC038H.010',
    uploaded: false,
    status: 'cancelled',
    createdAt: '2025-02-25T23:42:22.180Z',
    siteID: siteRefs.qdocVirtualHealthcare.id,
    payorID: payorRefs.mb.id,
    startingClaim: '000252210',
    cognitoID: '5c8d35a8-b0e1-706c-fbdf-87b1e93af252',
    submittedAt: null,
    s3Path: null,
    totalAmount: '29269.0',
    p1File: '',
    site: canonicalSiteByID.get(siteRefs.qdocVirtualHealthcare.id)!,
    payor: canonicalPayorByID.get(payorRefs.mb.id)!,
    claims: [],
  }),
  makeSubmissionSeed({
    id: '29b1ec34-78b2-465b-8329-dc9f026f0aac',
    filename: 'QDOC038H.005',
    uploaded: false,
    status: 'created',
    createdAt: '2025-02-19T22:47:25.215Z',
    siteID: siteRefs.qdocVirtualHealthcare.id,
    payorID: payorRefs.mb.id,
    startingClaim: '000250175',
    cognitoID: '5c9d35f8-c061-7065-5652-a9066bed47cd',
    submittedAt: null,
    s3Path: 'Submissions/submission-report-1739988941983.pdf',
    totalAmount: '11237044e7',
    p1File: '',
    site: canonicalSiteByID.get(siteRefs.qdocVirtualHealthcare.id)!,
    payor: canonicalPayorByID.get(payorRefs.mb.id)!,
    claims: [],
  }),
  makeSubmissionSeed({
    id: '754a56c9-a106-4be3-9473-ef6c9352b740',
    filename: 'QDOC038H-QDOC.019',
    uploaded: false,
    status: 'created',
    createdAt: '2025-03-20T21:45:16.677Z',
    siteID: siteRefs.qdocVirtualHealthcare.id,
    payorID: payorRefs.qdoc.id,
    startingClaim: '000252268',
    cognitoID: '5c8d35a8-b0e1-706c-fbdf-87b1e93af252',
    submittedAt: null,
    s3Path: null,
    totalAmount: null,
    p1File: '',
    site: canonicalSiteByID.get(siteRefs.qdocVirtualHealthcare.id)!,
    payor: canonicalPayorByID.get(payorRefs.qdoc.id)!,
    claims: [],
  }),
  makeSubmissionSeed({
    id: '7eb8bf21-3ca1-454a-9ad3-57b52a064521',
    filename: 'QDOC038H-MSH.006',
    uploaded: false,
    status: 'cancelled',
    createdAt: '2025-02-20T23:00:25.451Z',
    siteID: siteRefs.qdocVirtualHealthcare.id,
    payorID: payorRefs.msh.id,
    startingClaim: '000252199',
    cognitoID: '5c8d35a8-b0e1-706c-fbdf-87b1e93af252',
    submittedAt: null,
    s3Path: null,
    totalAmount: null,
    p1File: '',
    site: canonicalSiteByID.get(siteRefs.qdocVirtualHealthcare.id)!,
    payor: canonicalPayorByID.get(payorRefs.msh.id)!,
    claims: [],
  }),
] satisfies CanonicalSubmission[];

export const canonicalSubmissions: CanonicalSubmission[] =
  submissionSeeds.map(normalizeSubmission);

export const canonicalSubmissionByID = new Map(
  canonicalSubmissions.map((submission) => [submission.id, submission])
);
